import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Gift, Send, UserPlus, GraduationCap, Settings2, Copy, Loader2, Info, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import KanbanBoard, { StatStrip, CardTag, formatGBP, timeAgo } from '@/components/training/KanbanBoard';
import LeadDetailDrawer from "@/components/training/LeadDetailDrawer";

const ReferralsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchBoard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/training/referrals`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include',
      });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load referrals');
    } catch {
      toast.error('Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, [token, selectedBusiness?.business_id]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  const openSettings = () => {
    const st = data?.settings || {};
    setForm({
      referral_enabled: st.referral_enabled ?? true,
      referrer_credit: st.referrer_credit ?? 50,
      referee_discount: st.referee_discount ?? 100,
      referral_invite_webhook_url: st.referral_invite_webhook_url || '',
      referral_link_base: st.referral_link_base || '',
    });
    setSettingsOpen(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/training/growth-settings`, {
        method: 'PUT', headers: authHeaders, credentials: 'include',
        body: JSON.stringify({
          ...form,
          referrer_credit: parseFloat(form.referrer_credit) || 0,
          referee_discount: parseFloat(form.referee_discount) || 0,
        }),
      });
      if (res.ok) {
        toast.success('Referral settings saved');
        setSettingsOpen(false);
        fetchBoard();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (leadId) => {
    setBusyId(leadId);
    try {
      const res = await fetch(`${API_URL}/api/training/referrals/invite`, {
        method: 'POST', headers: authHeaders, credentials: 'include',
        body: JSON.stringify({ lead_id: leadId }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.message_sent) toast.success('Referral message sent');
      else if (res.ok) toast.warning(body.detail || 'Invite saved but no message sent');
      else toast.error(body.detail || 'Failed to send');
      fetchBoard();
    } finally {
      setBusyId(null);
    }
  };

  const setRewardStatus = async (rewardId, status) => {
    setBusyId(rewardId);
    try {
      const res = await fetch(`${API_URL}/api/training/referrals/rewards/${rewardId}`, {
        method: 'PUT', headers: authHeaders, credentials: 'include', body: JSON.stringify({ status }),
      });
      if (res.ok) { toast.success(status === 'used' ? 'Credit marked as used' : 'Credit made available again'); fetchBoard(); }
      else toast.error('Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  const createLinks = async () => {
    setBusyId('create-links');
    try {
      const res = await fetch(`${API_URL}/api/training/referrals/create-links`, { method: 'POST', headers: authHeaders, credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(body.created ? `Created referral links for ${body.created} students` : 'Every current student already has a link'); fetchBoard(); }
      else toast.error(body.detail || 'Failed to create links');
    } finally {
      setBusyId(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Copied'), () => toast.error('Could not copy'));
  };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>;
  }

  const st = data?.settings || {};
  const stats = data?.stats || {};
  const cols = data?.columns || {};
  const credit = formatGBP(st.referrer_credit);
  const discount = formatGBP(st.referee_discount);

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Referrals</h1>
          <p className="text-slate-500 mt-1">
            Every enrolled student is invited to refer a friend. They get {credit} off their next course, their friend gets {discount} off.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-xl h-9" onClick={fetchBoard} aria-label="Refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {canEdit && (
            <Button variant="outline" className="rounded-xl h-9" onClick={createLinks} disabled={busyId === 'create-links'}>
              <Copy className="w-4 h-4 mr-1" /> Create links for current students
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" className="rounded-xl h-9" onClick={openSettings}>
              <Settings2 className="w-4 h-4 mr-1" /> Referral settings
            </Button>
          )}
        </div>
      </div>

      {(!st.referral_enabled || !st.referral_invite_webhook_url) && (
        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" />
          <p>
            {!st.referral_enabled
              ? 'Referrals are switched off, so no invites are being created.'
              : 'Invites are being tracked, but no messages go out until the GHL referral workflow URL is added in Referral settings.'}
          </p>
        </div>
      )}

      <StatStrip items={[
        { label: 'Invited to refer', icon: Send, value: stats.invited ?? 0, sub: `${stats.messages_sent ?? 0} messages sent` },
        { label: 'Friends referred', icon: UserPlus, value: stats.friends_referred ?? 0 },
        { label: 'Friends enrolled', icon: GraduationCap, value: stats.friends_enrolled ?? 0, sub: `${formatGBP(stats.discounts_given)} in discounts given` },
        { label: 'Credit owed', icon: Gift, value: formatGBP(stats.credit_owed), sub: `${formatGBP(stats.credit_used)} already used` },
      ]} />

      <KanbanBoard columns={[
        {
          key: 'invited', title: 'Invited to refer', tone: 'blue', cards: cols.invited || [], count: (cols.invited || []).length,
          emptyText: 'Students appear here when they enrol',
          renderCard: (c) => (
            <div>
              <button type="button" onClick={() => setDrawerLeadId(c.lead_id)} className="text-sm font-semibold text-slate-900 truncate hover:text-pink-600 text-left block max-w-full">{c.name}</button>
              {c.course && <p className="text-xs text-slate-500 truncate">{c.course}</p>}
              <div className="flex flex-wrap gap-1 mt-2">
                <CardTag tone={c.message_sent ? 'green' : 'amber'}>{c.message_sent ? 'Message sent' : 'Not sent yet'}</CardTag>
                <CardTag>Code {c.referral_code}</CardTag>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-slate-400">{timeAgo(c.date)}</span>
                <div className="flex gap-1">
                  {c.referral_link && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copy(c.referral_link)}>
                      <Copy className="w-3 h-3 mr-1" /> Link
                    </Button>
                  )}
                  {canEdit && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyId === c.lead_id} onClick={() => resendInvite(c.lead_id)}>
                      <Send className="w-3 h-3 mr-1" /> {c.message_sent ? 'Resend' : 'Send'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ),
        },
        {
          key: 'referred', title: 'Friend referred', tone: 'violet', cards: cols.referred || [], count: (cols.referred || []).length,
          emptyText: 'Friends who sign up with a referral link',
          renderCard: (c) => (
            <div>
              <button type="button" onClick={() => setDrawerLeadId(c.lead_id)} className="text-sm font-semibold text-slate-900 truncate hover:text-pink-600 text-left block max-w-full">{c.name}</button>
              <p className="text-xs text-slate-500 truncate">Referred by {c.referred_by}</p>
              {c.course && <div className="mt-2"><CardTag>{c.course}</CardTag></div>}
              <p className="text-[11px] text-slate-400 mt-1.5">{timeAgo(c.date)} · gets {discount} off when they enrol</p>
            </div>
          ),
        },
        {
          key: 'friend_enrolled', title: 'Friend enrolled', tone: 'pink', cards: cols.friend_enrolled || [], count: (cols.friend_enrolled || []).length,
          subtitle: 'Credit waiting to be used',
          emptyText: 'Rewards appear when a referred friend enrols',
          renderCard: (c) => (
            <div>
              <p className="text-sm font-semibold text-slate-900 truncate">{c.referred_by}</p>
              <p className="text-xs text-slate-500 truncate">referred {c.name}</p>
              <div className="flex flex-wrap gap-1 mt-2">
                <CardTag tone="green">{formatGBP(c.referrer_credit)} credit</CardTag>
                <CardTag>{c.name} got {formatGBP(c.friend_discount)} off</CardTag>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-slate-400">{timeAgo(c.date)}</span>
                {canEdit && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyId === c.id} onClick={() => setRewardStatus(c.id, 'used')}>
                    Mark used
                  </Button>
                )}
              </div>
            </div>
          ),
        },
        {
          key: 'credit_used', title: 'Credit used', tone: 'green', cards: cols.credit_used || [], count: (cols.credit_used || []).length,
          emptyText: 'Credit is used automatically on the referrer\'s next enrolment',
          renderCard: (c) => (
            <div>
              <p className="text-sm font-semibold text-slate-900 truncate">{c.referred_by}</p>
              <p className="text-xs text-slate-500 truncate">for referring {c.name}</p>
              <div className="flex flex-wrap gap-1 mt-2"><CardTag tone="green">{formatGBP(c.referrer_credit)} used</CardTag></div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[11px] text-slate-400">{timeAgo(c.date)}</span>
                {canEdit && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" disabled={busyId === c.id} onClick={() => setRewardStatus(c.id, 'available')}>
                    Undo
                  </Button>
                )}
              </div>
            </div>
          ),
        },
      ]} />

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Referral settings</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4 py-1">
              <label className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-3 py-2.5 cursor-pointer">
                <span className="text-sm text-slate-700">Invite students to refer when they enrol</span>
                <Switch checked={form.referral_enabled} onCheckedChange={(v) => setForm(p => ({ ...p, referral_enabled: v }))} />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Referrer gets (£ credit)</label>
                  <Input type="number" min="0" step="1" value={form.referrer_credit} onChange={(e) => setForm(p => ({ ...p, referrer_credit: e.target.value }))} className="rounded-xl" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Friend gets (£ off)</label>
                  <Input type="number" min="0" step="1" value={form.referee_discount} onChange={(e) => setForm(p => ({ ...p, referee_discount: e.target.value }))} className="rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">GHL referral workflow URL</label>
                <Input value={form.referral_invite_webhook_url} onChange={(e) => setForm(p => ({ ...p, referral_invite_webhook_url: e.target.value }))} placeholder="https://services.leadconnectorhq.com/hooks/..." className="rounded-xl font-mono text-xs" />
                <p className="text-xs text-slate-500 mt-1">From a GHL workflow with an "Inbound Webhook" trigger. It sends the refer-a-friend SMS/email.</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Own sign-up page (optional)</label>
                <Input value={form.referral_link_base} onChange={(e) => setForm(p => ({ ...p, referral_link_base: e.target.value }))} placeholder="Leave blank to use the built-in page" className="rounded-xl font-mono text-xs" />
                <p className="text-xs text-slate-500 mt-1">
                  Leave blank and each student gets a ready-made sign-up page (app.sarumdigital.com/refer/THEIRCODE) that drops the friend straight into this CRM as their referral and into GHL tagged "referral".
                  Only fill this in if you want your own page (the link then gets <code>?ref=THEIRCODE</code> added).
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button className="btn-primary rounded-xl" onClick={saveSettings} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LeadDetailDrawer open={!!drawerLeadId} onOpenChange={(o) => { if (!o) setDrawerLeadId(null); }} leadId={drawerLeadId} onLeadUpdated={fetchBoard} />
    </div>
  );
};

export default ReferralsPage;
